// src/hooks/useNetworkStatus.ts
// Detects online/offline status and shows notifications

import { useState, useEffect, useCallback } from "react";
import { useSnackbar } from "notistack";
import { useTranslation } from "../i18n";

interface NetworkStatus {
  isOnline: boolean;
  wasOffline: boolean;
}

export const useNetworkStatus = (showNotifications = true): NetworkStatus => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [wasOffline, setWasOffline] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const handleOnline = useCallback(() => {
    setIsOnline(true);
    if (showNotifications && wasOffline) {
      enqueueSnackbar(t("network.connection_restored", "Connection restored"), {
        variant: "success",
        autoHideDuration: 3000,
        anchorOrigin: { vertical: "bottom", horizontal: "right" },
      });
    }
    setWasOffline(false);
  }, [showNotifications, wasOffline, enqueueSnackbar, t]);

  const handleOffline = useCallback(() => {
    setIsOnline(false);
    setWasOffline(true);
    if (showNotifications) {
      enqueueSnackbar(t("network.offline", "You are offline. Some features may not work."), {
        variant: "warning",
        autoHideDuration: 5000,
        anchorOrigin: { vertical: "bottom", horizontal: "right" },
      });
    }
  }, [showNotifications, enqueueSnackbar, t]);

  useEffect(() => {
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [handleOnline, handleOffline]);

  return { isOnline, wasOffline };
};

export default useNetworkStatus;
